// components/admin/ViewEnquiries.jsx
import React, { useEffect, useState } from "react";
import { getAllEnquiries, updateEnquiry } from "../../services/Enquiry";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
    Loader2,
    Edit3,
    Save,
    X,
    Search,
    AlertCircle,
} from "lucide-react";

export const ViewEnquiries = () => {
    const [enquiries, setEnquiries] = useState([]);
    const [filteredEnquiries, setFilteredEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [error, setError] = useState(null);

    const statusOptions = [
        "NEW",
        "IN_PROGRESS",
        "PENDING_INFO",
        "RESOLVED",
        "CLOSED",
        "SPAM",
    ];
    const priorityOptions = ["LOW", "MEDIUM", "HIGH"];

    // Fetch Enquiries
    const fetchEnquiries = async () => {
        try {
            setError(null);
            const response = await getAllEnquiries();
            if (response.success && Array.isArray(response.data)) {
                setEnquiries(response.data);
                setFilteredEnquiries(response.data);
            } else {
                throw new Error("Invalid response format");
            }
        } catch (err) {
            console.error("Error loading enquiries:", err);
            setError("Failed to load enquiries. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiries();
    }, []);

    // Apply filters and search
    useEffect(() => {
        let result = enquiries;
        if (statusFilter !== "ALL") {
            result = result.filter((enquiry) => enquiry.status === statusFilter);
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (enquiry) =>
                    enquiry.name?.toLowerCase().includes(term) ||
                    enquiry.email?.toLowerCase().includes(term) ||
                    enquiry.subject?.toLowerCase().includes(term) ||
                    enquiry.organization?.toLowerCase().includes(term)
            );
        }
        setFilteredEnquiries(result);
    }, [enquiries, statusFilter, searchTerm]);

    // Edit Handlers
    const handleEdit = (enquiry) => {
        setEditingId(enquiry.id);
        setEditData({
            status: enquiry.status || "NEW",
            adminNotes: enquiry.adminNotes || "",
            priority: enquiry.priority || "MEDIUM",
            assignedTo: enquiry.assignedTo || "",
        });
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleSave = async (id) => {
        try {
            await updateEnquiry(id, editData);
            setEditingId(null);
            fetchEnquiries();
        } catch (err) {
            console.error("Error updating enquiry:", err);
            setError("Failed to update enquiry. Please try again.");
        }
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
        });

    const getStatusColor = (status) => {
        const map = {
            NEW: "bg-blue-100 text-blue-800 border-blue-300",
            IN_PROGRESS: "bg-yellow-100 text-yellow-800 border-yellow-300",
            PENDING_INFO: "bg-orange-100 text-orange-800 border-orange-300",
            RESOLVED: "bg-green-100 text-green-800 border-green-300",
            CLOSED: "bg-gray-100 text-gray-800 border-gray-300",
            SPAM: "bg-red-100 text-red-800 border-red-300",
        };
        return map[status] || "bg-gray-100 text-gray-800 border-gray-300";
    };

    const getPriorityColor = (priority) => {
        const map = {
            LOW: "bg-green-100 text-green-800 border-green-300",
            MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-300",
            HIGH: "bg-red-100 text-red-800 border-red-300",
        };
        return map[priority] || "bg-gray-100 text-gray-800 border-gray-300";
    };

    return (
        <Card className="shadow-lg border border-gray-200 rounded-xl overflow-hidden">
            <CardHeader className="flex flex-col sm:flex-row justify-between items-center gap-3">
                <CardTitle className="font-semibold text-gray-800 flex items-center gap-2">
                    Client Enquiries
                </CardTitle>
                <span className="text-sm font-medium text-gray-800">
                    {enquiries.length} Total Enquiries
                </span>
            </CardHeader>

            <CardContent className="p-4">
                {/* Error Message */}
                {error && (
                    <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                )}

                {/* Search and Filters */}
                <div className="mb-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
                    <div className="relative flex-1 w-full">
                        {/* <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" /> */}
                        <input
                            type="text"
                            placeholder="   Search by name, email, or subject..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none text-sm"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
                    >
                        <option value="ALL">All Status</option>
                        {statusOptions.map((status) => (
                            <option key={status} value={status}>
                                {status.replace("_", " ")}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="py-8 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading enquiries...
                    </div>
                ) : filteredEnquiries.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                        {enquiries.length === 0
                            ? "No enquiries found."
                            : "No enquiries match your filters."}
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border">
                        <table className="min-w-full text-sm">
                            <thead className="sticky top-0 z-10 text-gray-700 uppercase text-xs border-b">
                                <tr>
                                    {[
                                        "Name / Organization",
                                        "Contact",
                                        "Subject",
                                        "Status",
                                        "Priority",
                                        "Notes",
                                        "Created",
                                        "Actions",
                                    ].map((head) => (
                                        <th key={head} className="px-4 py-3 text-left font-semibold">
                                            {head}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredEnquiries.map((enquiry) => (
                                    <tr
                                        key={enquiry.id}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        {/* Name */}
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-800">
                                                {enquiry.name}
                                            </div>
                                            {enquiry.organization && (
                                                <div className="text-xs text-gray-500">
                                                    {enquiry.organization}
                                                </div>
                                            )}
                                        </td>

                                        {/* Contact */}
                                        <td className="px-4 py-3 text-gray-700">
                                            <div className="text-blue-600 cursor-pointer hover:underline">
                                                {enquiry.email}
                                            </div>
                                            {enquiry.phone && (
                                                <div className="text-xs text-gray-500">
                                                    {enquiry.phone}
                                                </div>
                                            )}
                                        </td>

                                        {/* Subject */}
                                        <td className="px-4 py-3 max-w-xs truncate">
                                            <div className="font-medium">{enquiry.subject}</div>
                                            {enquiry.message && (
                                                <div
                                                    className="text-xs text-gray-500 truncate"
                                                    title={enquiry.message}
                                                >
                                                    {enquiry.message}
                                                </div>
                                            )}
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3">
                                            {editingId === enquiry.id ? (
                                                <select
                                                    className="border rounded px-2 py-1 w-full text-sm cursor-pointer"
                                                    value={editData.status}
                                                    onChange={(e) =>
                                                        setEditData({
                                                            ...editData,
                                                            status: e.target.value,
                                                        })
                                                    }
                                                >
                                                    {statusOptions.map((s) => (
                                                        <option key={s}>{s}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span
                                                    className={`px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(
                                                        enquiry.status
                                                    )}`}
                                                >
                                                    {enquiry.status || "NEW"}
                                                </span>
                                            )}
                                        </td>

                                        {/* Priority */}
                                        <td className="px-4 py-3">
                                            {editingId === enquiry.id ? (
                                                <select
                                                    className="border rounded px-2 py-1 w-full text-sm cursor-pointer"
                                                    value={editData.priority}
                                                    onChange={(e) =>
                                                        setEditData({
                                                            ...editData,
                                                            priority: e.target.value,
                                                        })
                                                    }
                                                >
                                                    {priorityOptions.map((p) => (
                                                        <option key={p}>{p}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span
                                                    className={`px-2 py-1 rounded-full border text-xs font-medium ${getPriorityColor(
                                                        enquiry.priority
                                                    )}`}
                                                >
                                                    {enquiry.priority}
                                                </span>
                                            )}
                                        </td>

                                        {/* Notes */}
                                        <td className="px-4 py-3 max-w-xs">
                                            {editingId === enquiry.id ? (
                                                <textarea
                                                    className="border rounded px-2 py-1 w-full text-sm"
                                                    rows="2"
                                                    value={editData.adminNotes}
                                                    onChange={(e) =>
                                                        setEditData({
                                                            ...editData,
                                                            adminNotes: e.target.value,
                                                        })
                                                    }
                                                    placeholder="Add notes..."
                                                />
                                            ) : (
                                                <div className="text-sm text-gray-700">
                                                    {enquiry.adminNotes || (
                                                        <span className="text-gray-400 italic">
                                                            No notes
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>

                                        {/* Created */}
                                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                            {formatDate(enquiry.createdAt)}
                                        </td>

                                        {/* Actions */}
                                        <td className="px-4 py-3">
                                            {editingId === enquiry.id ? (
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleSave(enquiry.id)}
                                                        className="bg-green-600 hover:bg-green-700 text-white cursor-pointer"
                                                    >
                                                        <Save className="w-3 h-3 mr-1" /> Save
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={handleCancel}
                                                        className="cursor-pointer"
                                                    >
                                                        <X className="w-3 h-3 mr-1" /> Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleEdit(enquiry)}
                                                    className="cursor-pointer"
                                                >
                                                    <Edit3 className="w-3 h-3 mr-1" /> Edit
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
